<template>
  <div
    class="flex flex-col"
    :class="[
      externalScroll ? '' : 'min-h-0 flex-1',
      pageHeading ? 'gap-0' : 'gap-4'
    ]"
  >
    <layout-page-head-section v-if="initialResolved && pageHeading && !empty">
      <div class="flex min-h-[5.25rem] flex-wrap items-center gap-x-4 gap-y-2 md:min-h-10 md:flex-nowrap md:gap-6">
        <layout-page-heading class="shrink-0">{{ pageHeading }}</layout-page-heading>

        <div class="order-3 h-9 w-full min-w-0 md:order-none md:w-auto md:flex-1">
          <div v-if="preferencesReady && selectedDecade" class="flex h-9 min-w-0 items-center gap-2">
            <u-button
              ref="backButton"
              icon="i-ri-arrow-left-line"
              label="Back to Decades"
              color="white"
              size="xl"
              variant="link"
              :padded="false"
              @click="showDecades"
            /> /
            <h2 class="truncate font-sans text-lg font-semibold text-slate-700">
              {{ formatTimelinePeriodLabel(selectedDecade) }}
            </h2>
          </div>

          <timeline-controls
            v-else-if="preferencesReady"
            class="h-9"
            :period-type="periodType"
            :sort-order="sortOrder"
            @update:period-type="$emit('update:periodType', $event)"
            @update:sort-order="$emit('update:sortOrder', $event)"
          />
        </div>

        <slot name="heading-actions" />
      </div>
    </layout-page-head-section>

    <div v-else-if="initialResolved && !pageHeading && preferencesReady && !empty && selectedDecade" class="flex h-9 min-w-0 shrink-0 items-center gap-2">
      <div class="flex min-w-0 items-center gap-2">
        <u-button
          ref="backButton"
          icon="i-ri-arrow-left-line"
          label="Back to Decades"
          color="white"
          size="xl"
          variant="link"
          :padded="false"
          @click="showDecades"
        /> /
        <h2 class="truncate font-sans text-lg font-semibold text-slate-700">
          {{ formatTimelinePeriodLabel(selectedDecade) }}
        </h2>
      </div>
    </div>

    <timeline-controls
      v-else-if="initialResolved && !pageHeading && preferencesReady && !empty"
      :period-type="periodType"
      :sort-order="sortOrder"
      @update:period-type="$emit('update:periodType', $event)"
      @update:sort-order="$emit('update:sortOrder', $event)"
    />

    <core-state-layout v-if="initialLoading || pending">
      <core-contextual-loading-box loading-text="Loading Timeline" sans-text />
    </core-state-layout>

    <core-state-layout v-else-if="error">
      <core-contextual-error-box :action="() => $emit('retry')" />
    </core-state-layout>

    <core-state-layout v-else-if="empty">
      <timeline-empty-state
        :can-upload="canUpload"
        :not-started="notStarted"
        @refresh="$emit('retry')"
      />
    </core-state-layout>

    <template v-else>
      <div :class="externalScroll ? 'timeline-browser__views' : 'timeline-browser__views grid min-h-0 flex-1'">
        <transition name="timeline-browser-fade">
          <div
            v-show="showingDecades"
            ref="decadesScrollParent"
            :class="externalScroll ? 'timeline-browser__view overflow-x-auto overflow-y-hidden pt-2 scrollbar-light' : 'timeline-browser__view col-start-1 row-start-1 min-h-0 overflow-y-auto pt-2 pr-1 scrollbar-light lg:overflow-auto lg:pr-0'"
            @scroll="emitScrollMetrics"
            @wheel.passive="emitUpwardIntent"
            @touchstart.passive="recordTouchStart"
            @touchmove.passive="emitTouchIntent"
          >
            <timeline-viewport
              ref="decadesViewport"
              :periods="decades"
              :show-actions="showPeriodActions"
              @select="selectPeriod"
              @action="$emit('periodAction', $event)"
            />
          </div>
        </transition>

        <transition name="timeline-browser-fade" @before-enter="resetYearsScroll">
          <div
            v-show="showingYears"
            ref="yearsScrollParent"
            :class="externalScroll ? 'timeline-browser__view overflow-x-auto overflow-y-hidden pt-2 scrollbar-light' : 'timeline-browser__view col-start-1 row-start-1 min-h-0 overflow-y-auto pt-2 pr-1 scrollbar-light lg:overflow-auto lg:pr-0'"
            @scroll="emitScrollMetrics"
            @wheel.passive="emitUpwardIntent"
            @touchstart.passive="recordTouchStart"
            @touchmove.passive="emitTouchIntent"
          >
            <core-contextual-loading-box
              v-if="decadePending"
              class="my-auto"
              loading-text="Loading Decade"
              sans-text
            />
            <core-contextual-error-box
              v-else-if="decadeError"
              class="my-auto"
              :action="retryDecade"
            />
            <timeline-viewport
              v-else
              :periods="visibleYears"
              :show-actions="showPeriodActions"
              @select="selectPeriod"
              @action="$emit('periodAction', $event)"
            />
          </div>
        </transition>
      </div>
    </template>
  </div>
</template>

<script setup>
  const props = defineProps({
    timeline: Object,
    pageHeading: String,
    pending: Boolean,
    error: [Object, Boolean],
    preferencesReady: Boolean,
    periodType: {
      type: String,
      required: true
    },
    sortOrder: {
      type: String,
      required: true
    },
    decades: {
      type: Array,
      default: () => []
    },
    years: {
      type: Array,
      default: () => []
    },
    selectedDecadeId: String,
    getYearsForDecade: {
      type: Function,
      required: true
    },
    scrollElement: Object,
    externalScroll: Boolean,
    showPeriodActions: Boolean,
    empty: Boolean,
    canUpload: Boolean,
    notStarted: Boolean
  });

  const emit = defineEmits([
    'retry',
    'select',
    'periodAction',
    'exit-decade',
    'scroll',
    'upward-intent',
    'update:periodType',
    'update:sortOrder'
  ]);

  const initialStatus = computed(() => {
    if (props.error) return 'error';
    return !props.pending && props.preferencesReady ? 'success' : 'pending';
  });
  const {initialResolved, initialLoading} = useInitialLoadState(initialStatus);

  const selectedDecade = ref(null);
  const selectedDecadeYears = ref([]);
  const decadePending = ref(false);
  const decadeError = ref(null);
  const backButton = ref(null);
  const decadesViewport = ref(null);
  const decadesScrollParent = ref(null);
  const yearsScrollParent = ref(null);
  const touchStartY = ref(null);
  const showingDecades = computed(() => !selectedDecade.value && props.periodType === 'decade');
  const showingYears = computed(() => Boolean(selectedDecade.value) || props.periodType === 'year');
  const visibleYears = computed(() => {
    if (!selectedDecade.value) {
      return props.years;
    }

    const years = selectedDecadeYears.value;

    return [...years]
      .sort((a, b) => (
        props.sortOrder === 'newest-to-oldest'
          ? b.value - a.value
          : a.value - b.value
      ))
      .map((year, index) => ({
        ...year,
        displayPosition: index % 2 === 0 ? 'top' : 'bottom'
      }));
  });

  let decadeRequest = null;
  let decadeGeneration = 0;
  onBeforeUnmount(() => { decadeGeneration++; });

  watch(() => props.periodType, () => {
    decadeGeneration++;
    decadePending.value = false;
    selectedDecade.value = null;
    selectedDecadeYears.value = [];
    decadeError.value = null;
  });

  watch(
    [() => props.selectedDecadeId, () => props.decades],
    async ([decadeId, decades]) => {
      if (decadeId === undefined) {
        if (selectedDecade.value) {
          selectedDecade.value = decades?.find(decade => decade.id === selectedDecade.value.id) || null;
        }
        return;
      }

      if (!decadeId) {
        if (selectedDecade.value) await closeDecade();
        return;
      }

      const decade = decades?.find(period => period.id === decadeId);
      if (!decade) return;
      if (selectedDecade.value?.id === decade.id) {
        selectedDecade.value = decade;
        return;
      }

      await openDecade(decade, {emitSelection: false, focusBack: false});
    },
    {immediate: true}
  );

  async function selectPeriod(period) {
    if (period.type === 'decade') {
      await openDecade(period);
      return;
    }

    emit('select', period);
  }

  async function showDecades() {
    if (props.selectedDecadeId !== undefined) {
      emit('exit-decade');
    }

    await closeDecade();
  }

  async function loadSelectedDecade(period, options) {
    const generation = ++decadeGeneration;
    decadeError.value = null;
    decadePending.value = true;
    const request = (async () => {
      try {
        const years = await props.getYearsForDecade(period, options);
        if (generation === decadeGeneration) selectedDecadeYears.value = years;
      } catch (error) {
        if (generation === decadeGeneration) decadeError.value = error;
      } finally {
        if (generation === decadeGeneration) decadePending.value = false;
      }
    })();
    decadeRequest = request;
    await request;
  }

  async function openDecade(period, {emitSelection = true, focusBack = true} = {}) {
    selectedDecade.value = period;
    selectedDecadeYears.value = [];
    if (emitSelection) emit('select', period);
    await loadSelectedDecade(period);

    if (focusBack && selectedDecade.value?.id === period.id) {
      await nextTick();
      const element = backButton.value?.$el || backButton.value;
      element?.focus({preventScroll: true});
    }
  }

  async function closeDecade() {
    decadeGeneration++;
    decadePending.value = false;
    const decadeId = selectedDecade.value?.id;
    resetYearsScroll();
    selectedDecade.value = null;
    selectedDecadeYears.value = [];
    decadeError.value = null;

    if (decadeId) {
      await nextTick();
      decadesViewport.value?.focusPeriod(decadeId);
    }
  }

  async function retryDecade() {
    if (!selectedDecade.value) return;

    await loadSelectedDecade(selectedDecade.value, {refresh: true});
  }

  function resetYearsScroll() {
    if (props.externalScroll) {
      return;
    }

    yearsScrollParent.value?.scrollTo({top: 0, left: 0, behavior: 'auto'});
  }

  function captureNavigationState() {
    if (props.externalScroll) {
      return {
        periodType: props.periodType,
        timelineId: props.timeline?.id,
        selectedDecadeId: selectedDecade.value?.id || null,
        scroll: {
          top: props.scrollElement?.scrollTop || 0,
          left: 0
        }
      };
    }

    return {
      periodType: props.periodType,
      timelineId: props.timeline?.id,
      selectedDecadeId: selectedDecade.value?.id || null,
      decadesScroll: {
        top: decadesScrollParent.value?.scrollTop || 0,
        left: decadesScrollParent.value?.scrollLeft || 0
      },
      yearsScroll: {
        top: yearsScrollParent.value?.scrollTop || 0,
        left: yearsScrollParent.value?.scrollLeft || 0
      }
    };
  }

  async function restoreNavigationState(state, {isCurrent = () => true} = {}) {
    if (!state || !isCurrent()) return false;

    const selectedDecadeId = props.selectedDecadeId !== undefined
      ? props.selectedDecadeId
      : state.selectedDecadeId;
    if (state.periodType !== props.periodType || state.selectedDecadeId !== (selectedDecadeId || null)) return false;
    if (selectedDecadeId && selectedDecade.value?.id !== selectedDecadeId) {
      const decade = props.decades.find(period => period.id === selectedDecadeId);
      if (!decade) return false;
      await openDecade(decade, {emitSelection: false, focusBack: false});
    }
    const generation = decadeGeneration;
    if (decadePending.value) await decadeRequest;
    await nextTick();
    if (!isCurrent() || generation !== decadeGeneration || decadeError.value || props.timeline?.id !== state.timelineId
      || props.periodType !== state.periodType || (selectedDecade.value?.id || null) !== (selectedDecadeId || null)) return false;
    if (props.externalScroll) {
      if (!props.scrollElement) return false;
      props.scrollElement?.scrollTo({...state.scroll, behavior: 'auto'});
      return true;
    }
    if (!getActiveScrollElement()) return false;
    decadesScrollParent.value?.scrollTo({...state.decadesScroll, behavior: 'auto'});
    yearsScrollParent.value?.scrollTo({...state.yearsScroll, behavior: 'auto'});
    return true;
  }

  function getActiveScrollTop() {
    return getActiveScrollMetrics().scrollTop;
  }

  function getActiveScrollElement() {
    if (props.externalScroll) {
      return props.scrollElement;
    }

    return showingYears.value
      ? yearsScrollParent.value
      : decadesScrollParent.value;
  }

  function getActiveScrollMetrics() {
    return getScrollMetrics(getActiveScrollElement());
  }

  function getScrollMetrics(element) {
    return {
      scrollTop: element?.scrollTop || 0,
      scrollHeight: element?.scrollHeight || 0,
      clientHeight: element?.clientHeight || 0
    };
  }

  function emitScrollMetrics(event) {
    emit('scroll', getScrollMetrics(event.currentTarget));
  }

  function emitUpwardIntent(event) {
    if (event.deltaY < 0 && event.currentTarget.scrollTop <= 80) {
      emit('upward-intent');
    }
  }

  function recordTouchStart(event) {
    touchStartY.value = event.touches[0]?.clientY ?? null;
  }

  function emitTouchIntent(event) {
    const currentY = event.touches[0]?.clientY;
    if (
      touchStartY.value !== null
      && currentY > touchStartY.value
      && event.currentTarget.scrollTop <= 80
    ) {
      emit('upward-intent');
    }
    touchStartY.value = currentY ?? touchStartY.value;
  }

  function adjustActiveScrollBy(delta) {
    const element = getActiveScrollElement();

    if (!element || !delta) {
      return;
    }

    element.scrollTop += delta;
    emit('scroll', getScrollMetrics(element));
  }

  defineExpose({
    captureNavigationState,
    restoreNavigationState,
    refreshSelectedDecade: retryDecade,
    getActiveScrollTop,
    getActiveScrollMetrics,
    adjustActiveScrollBy
  });
</script>

<style scoped>
  .timeline-browser-fade-enter-active,
  .timeline-browser-fade-leave-active {
    transition: opacity 180ms ease;
  }

  .timeline-browser-fade-enter-from,
  .timeline-browser-fade-leave-to {
    opacity: 0;
  }

  @media (prefers-reduced-motion: reduce) {
    .timeline-browser-fade-enter-active,
    .timeline-browser-fade-leave-active {
      transition: none;
    }
  }
</style>
